import type { MFRequestableRole } from "state/members/access_v2/catalogs";
import {
  recipientOptionKey,
  type RecipientOptions,
} from "state/members/access_v2/recipientRequestOptions";
import type { ResolvedRecipient } from "./BasketDrawer";
import {
  approvalTone,
  recipientRoles,
  requestableRoles,
  selectedRoleIds,
  type RecipientRoleSelection,
  type RoleSelection,
} from "./roleSelection";
import { resourceKey, type FlatRequestItem } from "./types";

// A basket row as one member may request it: the catalog read is the viewer's,
// so another member's roles and terms replace it. null when they can't request
// it at all.
export type ItemView = (
  memberId: string,
  item: FlatRequestItem,
) => FlatRequestItem | null;

export const catalogView: ItemView = (_memberId, item) => item;

// A member the options haven't answered for yet reads the catalog until they
// do; the basket holds the send meanwhile.
export const recipientOptionsView =
  (options: RecipientOptions | null, selfMemberId?: string): ItemView =>
  (memberId, item) => {
    if (!options || memberId === selfMemberId) return item;
    const resource =
      options[
        recipientOptionKey(memberId, {
          catalogItemId: item.catalogItemId,
          selectableResourceId: resourceKey(item),
        })
      ];
    if (resource === undefined) return item;
    if (resource === null) return null;
    return {
      ...item,
      resource: { ...item.resource, selectableResource: resource },
    };
  };

const unionRoles = (views: FlatRequestItem[]): MFRequestableRole[] => {
  const byId = new Map<string, MFRequestableRole>();
  for (const view of views)
    for (const role of requestableRoles(view) ?? [])
      if (!byId.has(role.id)) byId.set(role.id, role);
  return [...byId.values()];
};

// What a recipient chip offers on a row: a person's own view; for a group, the
// first member who can request it, offering every role any member may request.
// Each member is still sent only the roles they are offered.
export const recipientItemView = (
  recipient: ResolvedRecipient,
  item: FlatRequestItem,
  view: ItemView,
): FlatRequestItem | null => {
  if (recipient.memberIds.length === 0) return item;
  const views = recipient.memberIds
    .map((memberId) => view(memberId, item))
    .filter((v): v is FlatRequestItem => v !== null);
  const [first] = views;
  if (!first) return null;
  if (views.length === 1 || !requestableRoles(first)) return first;
  return {
    ...first,
    resource: {
      ...first.resource,
      selectableResource: {
        ...first.resource.selectableResource,
        entitlements: unionRoles(views),
      },
    },
  };
};

const roleChoices = (
  item: FlatRequestItem,
  selection: RecipientRoleSelection,
  recipientId: string,
) =>
  JSON.stringify([
    (requestableRoles(item) ?? []).map((r) => [r.id, r.name, r.terms]),
    [...selectedRoleIds(item, recipientRoles(selection, recipientId))].sort(),
  ]);

export type RoleSharing = { recipientIds: string[]; shared: boolean };

// One picker can serve every recipient who can request the row only when each
// is offered the same roles on the same terms AND has the same roles ticked:
// otherwise it would show one recipient's ticks as everyone's, and ticking on
// it would overwrite the others' own choices.
export const roleSharing = (
  recipients: ResolvedRecipient[],
  item: FlatRequestItem,
  recipientItem: (
    recipientId: string,
    item: FlatRequestItem,
  ) => FlatRequestItem | null,
  selection: RecipientRoleSelection,
): RoleSharing => {
  const able = recipients.flatMap((r) => {
    const view = recipientItem(r.id, item);
    return view
      ? [{ id: r.id, choices: roleChoices(view, selection, r.id) }]
      : [];
  });
  return {
    recipientIds: able.map((r) => r.id),
    shared: new Set(able.map((r) => r.choices)).size <= 1,
  };
};

// One status for a row several recipients share: it needs approval when anyone
// it is sent to needs it. null when it is sent to nobody.
export const sharedApprovalTone = (
  sent: { view: FlatRequestItem; selection: RoleSelection }[],
): "approval" | "instant" | null =>
  sent.length === 0
    ? null
    : sent.some(
          ({ view, selection }) => approvalTone(view, selection) === "approval",
        )
      ? "approval"
      : "instant";
